import React, { useCallback, useEffect, useState } from 'react';

import { MayBeNil } from 'app/@types/utils/may-be-nil';

import useStyles from './styles';

interface Props {
    id: string;
    offset?: number;
    element: MayBeNil<Element>;
    className?: string;
}

interface Parameters {
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
}

const SvgMask: React.FC<Props> = ({ id, element, offset = 0, className }) => {
    const { classes, cx } = useStyles();

    const calculate = useCallback(
        (element: MayBeNil<Element>) => {
            if (element) {
                const { x, y, width, height } = element.getBoundingClientRect();
                const radius = parseFloat(getComputedStyle(element).borderRadius) || 0;
                return {
                    x: x - offset,
                    y: y - offset,
                    width: width + 2 * offset,
                    height: height + 2 * offset,
                    radius: radius + offset
                };
            } else {
                return null;
            }
        },
        [offset]
    );

    const [parameters, setParameters] = useState<Parameters | null>(() => calculate(element));

    useEffect(() => {
        if (element) {
            setParameters(calculate(element));
            const observer = new ResizeObserver(([entry]) => setParameters(calculate(entry.target)));
            return () => observer.disconnect();
        } else {
            setParameters(null);
        }
    }, [calculate, element]);

    return parameters ? (
        <svg className={cx(classes.root, className)}>
            <mask id={id} maskUnits="userSpaceOnUse">
                <rect x={0} y={0} className={classes.backdrop} />
                <rect
                    x={parameters.x}
                    y={parameters.y}
                    width={parameters.width}
                    height={parameters.height}
                    rx={parameters.radius}
                    ry={parameters.radius}
                    className={classes.window}
                />
            </mask>
            <rect
                x={parameters.x}
                y={parameters.y}
                width={parameters.width}
                height={parameters.height}
                rx={parameters.radius}
                ry={parameters.radius}
                className={classes.outline}
            />
        </svg>
    ) : null;
};

export default SvgMask;
